/* Staged drawing release to the Task Mind Map.
   Drawings go to the board one design stage at a time — Schematic Design first,
   and the next stage only unlocks once the client has accepted the one before.
   Each stage's drawings are intelligently scheduled (sequenced + dated) so the
   dates flow straight into this project's calendar. Pure logic, no React. */

import type { Project, Deliverable, DeliverablePhase, StageKey } from "./types";

export interface DrawStage {
  key: "SD" | "DD" | "CD";
  label: string;
  phase: DeliverablePhase;   // the register phase this stage releases
  projectStage: StageKey;    // the project stage whose acceptance gates the NEXT release
  weeks: number;             // nominal production window, drives the schedule spread
}

/** The three drawing stages, in release order. */
export const DRAW_STAGES: DrawStage[] = [
  { key: "SD", label: "Schematic Design",       phase: "Conceptual",         projectStage: "concept",       weeks: 5 },
  { key: "DD", label: "Design Development",      phase: "Design Development", projectStage: "developed",     weeks: 7 },
  { key: "CD", label: "Construction Documents",  phase: "IFT",                projectStage: "documentation", weeks: 10 },
];

export function stageByKey(key: string): DrawStage | undefined {
  return DRAW_STAGES.find((s) => s.key === key);
}

/** A project stage counts as accepted once it's marked done. */
export function stageAccepted(p: Project, projectStage: StageKey): boolean {
  return (p.stages ?? []).some((s) => s.key === projectStage && s.status === "done");
}

export function stageSentAt(p: Project, key: string): string | undefined {
  return p.mindmap?.stages?.[key]?.sentAt;
}

export type StageState =
  | { status: "sent"; at: string }
  | { status: "ready" }
  | { status: "locked"; reason: string };

/** Where each drawing stage stands: already on the board, ready to send, or
 *  locked until the prior stage is accepted (or the brief approved). */
export function stageStates(p: Project): { stage: DrawStage; state: StageState }[] {
  const briefOk = p.brief?.status === "approved";
  return DRAW_STAGES.map((stage, i) => {
    const at = stageSentAt(p, stage.key);
    if (at) return { stage, state: { status: "sent", at } as StageState };
    if (!briefOk) return { stage, state: { status: "locked", reason: "the brief has to be approved first" } };
    if (i > 0) {
      const prior = DRAW_STAGES[i - 1];
      if (!stageSentAt(p, prior.key)) return { stage, state: { status: "locked", reason: `send ${prior.label} first` } };
      if (!stageAccepted(p, prior.projectStage)) return { stage, state: { status: "locked", reason: `unlocks once the client accepts ${prior.label}` } };
    }
    return { stage, state: { status: "ready" } };
  });
}

/** The one stage that can be released right now (or null with the reason). */
export function releasableStage(p: Project): { stage: DrawStage | null; reason?: string } {
  const states = stageStates(p);
  const ready = states.find((s) => s.state.status === "ready");
  if (ready) return { stage: ready.stage };
  const nextLocked = states.find((s) => s.state.status === "locked");
  if (nextLocked && nextLocked.state.status === "locked") return { stage: null, reason: nextLocked.state.reason };
  return { stage: null, reason: "every drawing stage is on the board" };
}

/* ---------------------------------------------------- intelligent schedule */

const DISC_ORDER = ["Architecture", "Interiors", "Structural", "MEP"];
/** Canonical production order: lead discipline first, then by sheet number, so
 *  a plan is scheduled before the section and details that depend on it. */
function orderKey(d: Deliverable): number {
  const disc = DISC_ORDER.indexOf(d.discipline);
  const num = parseInt((d.code.match(/\d+/) || ["999"])[0], 10);
  // documents/schedules (cover, notes) lead; visuals trail slightly
  const kindBias = d.kind === "document" ? -1 : d.kind === "visual" ? 1 : 0;
  return (disc < 0 ? 9 : disc) * 100000 + kindBias * 10000 + num;
}

export interface ScheduledDrawing { id: string; code: string; title: string; due: string; }

/** Spread a stage's drawings across its production window: earlier sheets due
 *  sooner, the last landing at the stage end. Deterministic — same input, same
 *  dates — so it's safe to re-run. */
export function scheduleStage(drawings: Deliverable[], startISO: string, weeks: number): ScheduledDrawing[] {
  const ordered = [...drawings].sort((a, b) => orderKey(a) - orderKey(b));
  const start = new Date(startISO + "T00:00:00");
  const totalDays = Math.max(7, Math.round(weeks * 7));
  const n = ordered.length;
  return ordered.map((d, i) => {
    // first sheet ~25% in, last at 100% of the window
    const frac = n <= 1 ? 1 : i / (n - 1);
    const day = Math.round(totalDays * (0.25 + 0.75 * frac));
    const due = new Date(start);
    due.setDate(due.getDate() + day);
    // nudge weekend due-dates to the following Monday
    const dow = due.getDay();
    if (dow === 6) due.setDate(due.getDate() + 2);
    else if (dow === 0) due.setDate(due.getDate() + 1);
    return { id: d.id, code: d.code, title: d.title, due: due.toISOString().slice(0, 10) };
  });
}

/** When a stage's clock starts: the day its gate opened — the prior stage's
 *  acceptance, or the brief approval for Schematic Design. */
export function stageStart(p: Project, stage: DrawStage, today: string): string {
  const i = DRAW_STAGES.findIndex((s) => s.key === stage.key);
  if (i > 0) {
    const prior = DRAW_STAGES[i - 1];
    const priorStage = (p.stages ?? []).find((s) => s.key === prior.projectStage);
    if (priorStage?.completedOn) return priorStage.completedOn.slice(0, 10);
  }
  return (p.brief?.approvedOn || p.brief?.updatedOn || today).slice(0, 10);
}
